<template>
    <div class="article_details" v-loading="loading">
        <!-- 加载中骨架屏 -->
        <div v-if="loading" class="article-skeleton">
            <div class="skeleton-title"></div>
            <div class="skeleton-meta">
                <span class="skeleton-block"></span>
                <span class="skeleton-block"></span>
            </div>
            <div class="skeleton-content">
                <div class="skeleton-line" style="width: 100%"></div>
                <div class="skeleton-line" style="width: 95%"></div>
                <div class="skeleton-line" style="width: 98%"></div>
                <div class="skeleton-line" style="width: 90%"></div>
                <div class="skeleton-line" style="width: 85%"></div>
                <div class="skeleton-line" style="width: 92%"></div>
                <div class="skeleton-line" style="width: 88%"></div>
            </div>
        </div>

        <!-- 加载失败 -->
        <div v-else-if="loadError" class="error-container">
            <div class="error-icon">!</div>
            <div class="error-title">文章加载失败</div>
            <div class="error-desc">网络连接异常或文章已被删除</div>
            <el-button type="primary" icon="el-icon-refresh" @click="loadArticle">重新加载</el-button>
            <el-button plain @click="goBack">返回上一页</el-button>
        </div>

        <!-- 文章内容 -->
        <div v-else-if="db.title || db.noticeTitle" class="content_wrap">
            <div class="title">{{ db.title || db.noticeTitle }}</div>
            <div class="meta">
                <span v-if="db.author">作者：{{ db.author }}</span>
                <span v-if="db.createTime">{{ db.createTime }}</span>
                <span v-if="db.updateTime">{{ db.updateTime }}</span>
                <span v-if="db.clickNum">阅读：{{ db.clickNum }}</span>
            </div>
            <div class="content" v-html="db.content || db.noticeContent"></div>
        </div>

        <!-- 文章不存在 -->
        <div v-else class="empty-container">
            <div class="empty-icon">📄</div>
            <div class="empty-text">文章不存在或已被删除</div>
            <el-button type="primary" plain @click="goBack">返回上一页</el-button>
        </div>
    </div>
</template>

<script>
import { selectCmsArticleDetail } from '@/api/home/index'
export default {
    name: 'ArticleDetails',
    data() {
        return {
            db: {},
            loading: false,
            loadError: false,
        }
    },
    created() {
        this.loadArticle()
    },
    methods: {
        // 【P2-8 修复】加载文章，增加 loading/error 状态
        async loadArticle() {
            this.loading = true
            this.loadError = false
            try {
                const res = await selectCmsArticleDetail({ id: this.$route.query.id, type: this.$route.query.type })
                if (res && res.code === 200 && res.data) {
                    this.db = res.data
                } else if (res && res.code !== 200) {
                    // 业务错误
                    this.loadError = true
                    console.warn('文章加载业务错误:', res.msg)
                } else {
                    // 无数据
                    this.db = {}
                }
            } catch (err) {
                console.error('文章加载失败:', err)
                this.loadError = true
                this.db = {}
            } finally {
                this.loading = false
            }
        },

        // 返回上一页
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
            } else {
                this.$router.push('/')
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.article_details {
    // 【P2-8 修复】响应式布局：固定宽度改为最大宽度
    max-width: 1200px;
    width: 100%;
    margin: 20px auto;
    padding: 0 20px;
    box-sizing: border-box;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    .content_wrap {
        padding: 30px;

        .title {
            font-size: 28px;
            font-weight: bold;
            color: #333;
            margin-bottom: 16px;
            line-height: 1.4;
            word-break: break-all;
        }

        .meta {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid #eee;
            margin-bottom: 24px;
            font-size: 14px;
            color: #999;
        }

        .content {
            font-size: 16px;
            line-height: 1.8;
            color: #333;
            word-break: break-word;

            // 富文本内容图片自适应
            :deep(img) {
                max-width: 100%;
                height: auto;
                display: block;
                margin: 16px auto;
                border-radius: 4px;
            }

            // 富文本表格自适应
            :deep(table) {
                max-width: 100%;
                overflow-x: auto;
                display: block;
            }

            :deep(p) {
                margin-bottom: 16px;
            }

            :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
                margin-top: 24px;
                margin-bottom: 12px;
                font-weight: bold;
            }

            :deep(blockquote) {
                border-left: 4px solid #409eff;
                padding-left: 16px;
                color: #666;
                background: #f5f7fa;
                padding: 12px 16px;
                border-radius: 4px;
                margin: 16px 0;
            }

            :deep(pre) {
                background: #f5f7fa;
                padding: 16px;
                border-radius: 4px;
                overflow-x: auto;
                font-size: 14px;
            }
        }
    }

    // 【P2-8 修复】骨架屏样式
    .article-skeleton {
        padding: 30px;

        .skeleton-title {
            height: 32px;
            width: 60%;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: skeleton-loading 1.5s infinite;
            border-radius: 4px;
            margin-bottom: 16px;
        }

        .skeleton-meta {
            display: flex;
            gap: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid #eee;
            margin-bottom: 24px;

            .skeleton-block {
                display: inline-block;
                height: 14px;
                width: 100px;
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: skeleton-loading 1.5s infinite;
                border-radius: 3px;
            }
        }

        .skeleton-content {
            .skeleton-line {
                height: 16px;
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: skeleton-loading 1.5s infinite;
                border-radius: 3px;
                margin-bottom: 12px;
            }
        }
    }

    @keyframes skeleton-loading {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }

    // 【P2-8 修复】错误状态
    .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 20px;
        color: #666;

        .error-icon {
            width: 60px;
            height: 60px;
            line-height: 60px;
            border-radius: 50%;
            background-color: #fef0f0;
            color: #f56c6c;
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 20px;
        }
        .error-title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            margin-bottom: 8px;
        }
        .error-desc {
            font-size: 14px;
            color: #999;
            margin-bottom: 24px;
        }
        .el-button + .el-button {
            margin-top: 10px;
        }
    }

    // 【P2-8 修复】空状态
    .empty-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 20px;
        color: #666;

        .empty-icon {
            font-size: 48px;
            margin-bottom: 16px;
            opacity: 0.5;
        }
        .empty-text {
            font-size: 16px;
            color: #999;
            margin-bottom: 20px;
        }
    }

    // 【P2-8 修复】响应式适配
    @media screen and (max-width: 768px) {
        margin: 10px auto;
        padding: 0 10px;
        border-radius: 6px;

        .content_wrap {
            padding: 20px 16px;

            .title {
                font-size: 22px;
            }

            .meta {
                font-size: 12px;
                gap: 12px;
                flex-direction: column;
            }

            .content {
                font-size: 15px;
                line-height: 1.7;
            }
        }

        .article-skeleton {
            padding: 20px 16px;

            .skeleton-title {
                height: 26px;
                width: 80%;
            }
        }

        .error-container, .empty-container {
            padding: 60px 16px;
        }
    }

    @media screen and (max-width: 480px) {
        .content_wrap {
            padding: 16px 12px;

            .title {
                font-size: 20px;
            }
        }
    }
}
</style>
